<template>
  <div>
    <v-row dense>
      <v-col v-for="card in kpiCards" :key="card.key" cols="12" sm="6" md="3">
        <v-card outlined class="kpi-card">
          <v-card-text class="py-2">
            <div class="text-caption grey--text text--darken-1">{{ card.titulo }}</div>
            <v-skeleton-loader v-if="loading && !kpis" type="text" class="mt-2" />
            <template v-else>
              <div class="text-h6 font-weight-bold" :style="card.cor ? { color: card.cor } : null">
                {{ card.valor }}
              </div>
              <div class="text-caption grey--text text--darken-1">{{ card.detalhe }}</div>
            </template>
          </v-card-text>
          <v-progress-linear v-if="loading && kpis" indeterminate height="2" />
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="kpis" dense>
      <v-col cols="12" class="py-0 text-caption grey--text text--darken-1">
        <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
        {{ kpis.calculado && kpis.dt_calculo
          ? $t('externalAddress.kpis.calculatedAt', { date: formatDataHora(kpis.dt_calculo) })
          : $t('externalAddress.kpis.notCalculated') }}
      </v-col>
    </v-row>
    <v-row v-if="kpiTurnos.length" dense>
      <v-col cols="12">
        <v-tooltip v-for="t in kpiTurnos" :key="'kpi-turno-' + t.id_turno" top>
          <template v-slot:activator="{ on }">
            <v-chip small label outlined class="mr-2 mb-1" v-on="on">
              <strong class="mr-1">{{ t.cod }}:</strong>
              <span :style="{ color: corValido }">{{ pct(t.pct_validos) }}</span>
              <span class="grey--text ml-1">({{ t.validos }}/{{ kpis.geral.total }})</span>
            </v-chip>
          </template>
          <div>{{ $t('externalAddress.kpis.shiftTooltip', {
            shift: t.descricao, validated: t.validados, pct: pct(t.pct_validos_dos_validados) }) }}</div>
        </v-tooltip>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { COR_VALIDACAO, formatPct, formatTempoValidacao } from "@/utils/kpisEnderecosExternos";

/**
 * Cartões de KPI de validação de endereços externos (resposta de getkpisenderecosexternos),
 * usados pela aba Endereços Externos do CRUD de Candidatos e pelo relatório
 * RELKpisEnderecosExternos.
 */
export default {
  name: "KpisEnderecosExternosCards",
  props: {
    /** Resposta de getkpisenderecosexternos; null enquanto não carregou. */
    kpis: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /** Turnos que ganham chip, na ordem em que aparecem (id_turno, cod_turno, descricao_turno). */
    turnos: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    locale() {
      return this.$i18n && this.$i18n.locale;
    },
    corValido() {
      return COR_VALIDACAO[1];
    },
    kpiCards() {
      const g = (this.kpis && this.kpis.geral) || {};
      const n = (v) => Number(v || 0).toLocaleString(this.locale);
      return [
        {
          key: 'total',
          titulo: this.$t('externalAddress.kpis.total'),
          valor: n(g.total),
          detalhe: this.$t('externalAddress.kpis.totalHint'),
        },
        {
          key: 'validos',
          titulo: this.$t('externalAddress.kpis.valid'),
          valor: this.pct(g.pct_validos),
          cor: COR_VALIDACAO[1],
          detalhe: this.$t('externalAddress.kpis.validHint', {
            count: n(g.validos), pct: this.pct(g.pct_validos_dos_validados) }),
        },
        {
          key: 'rejeitados',
          titulo: this.$t('externalAddress.kpis.rejected'),
          valor: this.pct(g.pct_rejeitados),
          cor: COR_VALIDACAO[4],
          detalhe: this.$t('externalAddress.kpis.rejectedHint', { count: n(g.rejeitados), total: n(g.total) }),
        },
        {
          key: 'tempoMedio',
          titulo: this.$t('externalAddress.kpis.avgValidationTime'),
          valor: formatTempoValidacao(g.tempo_medio_validacao_ms, this.locale) || '-',
          detalhe: this.$t('externalAddress.kpis.avgValidationTimeHint', { count: n(g.validacoes_com_tempo) }),
        },
      ];
    },
    /** KPI por turno, só dos turnos recebidos (na ordem da lista). */
    kpiTurnos() {
      if (!this.kpis || !Array.isArray(this.kpis.turnos)) return [];
      const porId = new Map(this.kpis.turnos.map((t) => [Number(t.id_turno), t]));
      return (this.turnos || []).map((t) => ({
        id_turno: t.id_turno,
        validados: 0,
        validos: 0,
        pct_validos: 0,
        pct_validos_dos_validados: 0,
        ...(porId.get(Number(t.id_turno)) || {}),
        cod: t.cod_turno,
        descricao: t.descricao_turno || t.cod_turno,
      }));
    },
  },
  methods: {
    pct(v) {
      return formatPct(v, this.locale);
    },
    formatDataHora(iso) {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(this.locale);
    },
  },
};
</script>

<style scoped>
.kpi-card {
  border-radius: 10px;
}
</style>
